import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { supabase } from '@/lib/supabase';

interface Project {
  id: string;
  name: string;
  status: string;
  scrum_active: boolean;
  _client_name?: string;
}

const STATUS_COLOR: Record<string, string> = {
  planejamento: 'bg-slate-100 text-slate-600',
  ativo: 'bg-emerald-100 text-emerald-700',
  pausado: 'bg-orange-100 text-orange-700',
  concluido: 'bg-sky-100 text-sky-700',
  cancelado: 'bg-red-100 text-red-700',
};

export default function ScrumIndexPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: projs } = await supabase
        .from('projects')
        .select('id, name, status, scrum_active, client_id')
        .eq('scrum_active', true)
        .order('name');

      if (!projs || projs.length === 0) {
        setProjects([]);
        setLoading(false);
        return;
      }

      const clientIds = [...new Set(projs.map((p) => p.client_id).filter(Boolean))];
      const { data: clients } = clientIds.length > 0
        ? await supabase.from('clients').select('id, name').in('id', clientIds)
        : { data: [] };

      const clientMap: Record<string, string> = {};
      (clients || []).forEach((c) => { clientMap[c.id] = c.name; });

      setProjects(projs.map((p) => ({ ...p, _client_name: clientMap[p.client_id] || '—' })));
      setLoading(false);
    };
    load();
  }, []);

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-slate-800 text-2xl font-bold">Scrum</h1>
        <p className="text-slate-500 text-sm mt-1">Selecione um projeto para gerenciar o backlog e o board</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-dashboard-2-line text-5xl mb-4"></i>
          <p className="text-base font-medium text-slate-500">Nenhum projeto com Scrum ativo</p>
          <p className="text-sm mt-1">Ative o Scrum em um projeto para começar</p>
          <button
            onClick={() => navigate('/admin/projetos')}
            className="mt-5 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            Ir para Projetos
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-cyan-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-violet-50 border border-violet-100 shrink-0">
                  <i className="ri-dashboard-2-line text-violet-500 text-lg"></i>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[p.status] || 'bg-slate-100 text-slate-600'}`}>
                  {p.status}
                </span>
              </div>
              <h3 className="text-slate-800 font-semibold text-sm mb-0.5">{p.name}</h3>
              <p className="text-slate-400 text-xs mb-4">{p._client_name}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/admin/scrum/${p.id}/backlog`)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check-2 text-sm"></i>
                  Backlog
                </button>
                <button
                  onClick={() => navigate(`/admin/scrum/${p.id}/board`)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-kanban-view text-sm"></i>
                  Board
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
